<template>
  <div
    class="member-row"
    :class="{ 'member-row--actionable': rowActionable }"
    :role="rowActionable ? 'button' : undefined"
    :tabindex="rowActionable ? 0 : undefined"
    @click="onRowClick"
    @keydown.enter="rowActionable && onRowClick($event)"
    @keydown.space.prevent="rowActionable && onRowClick($event)"
  >
    <!--
      The initials sit underneath and the photo is layered on top, so a member
      without a picture — or with one that fails to load — always degrades to a
      readable coloured monogram instead of a broken image. The whole cluster is
      decorative: the name, role and tier are all exposed as text next to it.
    -->
    <div class="avatar" :style="avatarStyle" aria-hidden="true">
      <f7-icon v-if="!initials" f7="person_fill" size="18" />
      <template v-else>{{ initials }}</template>
      <img
        v-if="showAvatarImage"
        class="avatar-img"
        :src="avatarSrc"
        alt=""
        loading="lazy"
        decoding="async"
        referrerpolicy="no-referrer"
        @error="onAvatarError"
      />
      <div v-if="tier?.icon" class="tier-badge" :style="{ background: tier.color }">
        <f7-icon :f7="tier.icon" size="9" style="color: #fff" />
      </div>
    </div>

    <div class="member-info">
      <div v-if="displayName" class="member-name" :title="displayName">
        {{ displayName }}
      </div>
      <div v-if="contactLine" class="member-contact">
        <f7-icon :f7="contactLine.icon" size="10" />
        <span class="member-contact-text">{{ contactLine.text }}</span>
      </div>
      <div class="member-meta">
        <span
          v-if="role"
          class="role-chip"
          :class="role.toLowerCase()"
        >{{ $t(`member.role.${role}`) }}</span>
        <span
          v-if="tier"
          class="tier-label"
          :style="{ color: tier.color }"
        >{{ $t(`member.tier.${tier.code}`) }}</span>
        <span
          v-if="trustBadge"
          class="growth-chip"
          :style="{ color: trustBadge.color, background: trustBadge.bg }"
        >
          <f7-icon :f7="trustBadge.icon" size="9" /> {{ $t(trustBadge.label) }}
        </span>
        <span
          v-if="achievementCount > 0"
          class="growth-chip growth-chip--achievement"
        >
          <f7-icon f7="rosette" size="9" /> {{ achievementCount }}
        </span>
      </div>
    </div>

    <f7-button
      v-if="showNudge"
      small outline round
      class="nudge-btn"
      @click.stop="$emit('nudge', member)"
    >{{ $t('payout.nudge') }}</f7-button>

    <button
      v-if="canManage"
      type="button"
      class="menu-btn"
      :aria-label="$t('member.actions')"
      @click.stop="onMenuClick"
    >
      <f7-icon f7="ellipsis_vertical" size="18" />
    </button>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { tierForScore } from '../../constants/engagement-tiers.js'
import { trustMeta } from '../../constants/growth-levels.js'

const props = defineProps({
  member: { type: Object, required: true },
  showNudge: { type: Boolean, default: false },
  canManage: { type: Boolean, default: false },
  // Tapping the whole row is a shortcut for the same menu the `menu-btn` opens.
  // Callers set this only where that menu actually exists, so rows a viewer
  // cannot act on are not styled as interactive.
  rowActionable: { type: Boolean, default: false },
})
const emit = defineEmits(['open-actions', 'open-menu', 'nudge'])

const onRowClick = (event) => {
  if (!props.rowActionable) return
  emit('open-actions', props.member, event?.currentTarget ?? null)
}

// The menu opens as an Action Sheet converted to a Popover on large screens,
// anchored to the button that triggered it.
const onMenuClick = (event) => {
  emit('open-menu', props.member, event.currentTarget)
}

// Dark text on a light tint, so the monogram stays legible in both themes.
const AVATAR_PALETTE = [
  { bg: '#EAF3DE', fg: '#27500A' },
  { bg: '#EBF0FC', fg: '#0C447C' },
  { bg: '#FAEEDA', fg: '#633806' },
  { bg: '#FBEAF0', fg: '#72243E' },
  { bg: '#F1EFE8', fg: '#444441' },
]

// The account API falls back to the platform logo and to placeholder art, which
// would make every member look identical. Those are not personal photos.
const PLACEHOLDER_AVATAR = /avocash\.svg|placeholder|default[-_]?avatar|no[-_]?photo/i

function hashCode(value) {
  let h = 0
  for (let i = 0; i < value.length; i++) h = (h * 31 + value.charCodeAt(i)) >>> 0
  return h
}

const name = computed(() => String(props.member.name ?? '').trim())
const username = computed(() => String(props.member.username ?? '').trim())

const displayName = computed(() => name.value || (username.value ? `@${username.value}` : ''))

/**
 * Up to two letters: the initials for a two-word name, the first two letters for
 * a single-word one, and first + last for anything longer ("Jean Paul Sartre" →
 * JS, not JP). Array.from keeps the split code-point aware.
 */
const initials = computed(() => {
  const words = (name.value || username.value).split(/\s+/).filter(Boolean)
  if (words.length === 0) return ''
  const first = Array.from(words[0])
  if (words.length === 1) return (first[0] + (first[1] || '')).toUpperCase()
  const lastWord = Array.from(words[words.length - 1])
  return ((first[0] || '') + (lastWord[0] || '')).toUpperCase()
})

const avatarSrc = computed(() => {
  const raw = String(props.member.avatar ?? props.member.avatarUrl ?? '').trim()
  if (!raw) return ''
  if (raw.startsWith('data:') || raw.startsWith('blob:')) return raw
  // Relative paths, protocol-relative URLs and anything unresolvable are dropped
  // rather than triggering a failing request per row.
  if (!/^(https?:)?\/\//i.test(raw) && !raw.startsWith('/')) return ''
  if (PLACEHOLDER_AVATAR.test(raw)) return ''
  return raw
})

const avatarBroken = ref(false)
const showAvatarImage = computed(() => Boolean(avatarSrc.value) && !avatarBroken.value)
const onAvatarError = () => { avatarBroken.value = true }
// A new URL for the same member gets a fresh attempt.
watch(avatarSrc, () => { avatarBroken.value = false })

const avatarStyle = computed(() => {
  // Hashing a stable identity keeps the colour constant across renders and
  // reloads, and mixing in the name avoids one shared colour for members whose
  // userId is missing.
  const key = String(props.member.userId ?? '') || username.value || name.value
  const { bg, fg } = AVATAR_PALETTE[hashCode(key) % AVATAR_PALETTE.length]
  return { '--avatar-bg': bg, '--avatar-fg': fg }
})

// The members endpoint sends `phoneNumber`, and email/phone are not populated
// there yet — fall back through the shapes and show nothing when we have no
// contact detail at all rather than an icon next to an empty line.
const contactLine = computed(() => {
  const phone = String(props.member.phone ?? props.member.phoneNumber ?? '').trim()
  if (phone) return { icon: 'phone_fill', text: phone }
  const email = String(props.member.email ?? '').trim()
  if (email) return { icon: 'envelope_fill', text: email }
  if (username.value) return { icon: 'person_fill', text: `@${username.value}` }
  return null
})

const role = computed(() => String(props.member.role ?? '').trim().toUpperCase())

const tier = computed(() => tierForScore(props.member.engagementScore))

const trustBadge = computed(() =>
  props.member.trustLevel ? trustMeta(props.member.trustLevel) : null)

const achievementCount = computed(() => {
  if (Array.isArray(props.member.achievements)) return props.member.achievements.length
  return props.member.achievementCount || 0
})
</script>

<style scoped>
.member-row {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 4px; border-bottom: 1px solid var(--f7-list-item-border-color, #e0e0e0);
  color: var(--f7-on-surface, #1c1c1e);
}
.member-row--actionable { cursor: pointer; }
.member-row:last-child { border-bottom: none; }
.avatar {
  width: 40px; height: 40px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 600; letter-spacing: .2px;
  flex-shrink: 0; position: relative;
  background: var(--avatar-bg, var(--f7-list-item-bg-color, #eee));
  color: var(--avatar-fg, var(--f7-list-item-text-color, #444));
}
.avatar-img {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  border-radius: 50%;
  object-fit: cover;
}
.tier-badge {
  position: absolute; bottom: -2px; right: -2px;
  width: 16px; height: 16px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  border: 2px solid var(--f7-card-bg-color, #fff);
}
.member-info { flex: 1; min-width: 0; }
.member-name {
  font-size: 13px; font-weight: 500;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.member-contact {
  font-size: 11px; color: var(--f7-on-surface-variant, #8e8e93);
  display: flex; align-items: center; gap: 4px; margin-top: 1px;
  min-width: 0;
}
.member-contact-text {
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.member-meta {
  display: flex; align-items: center; flex-wrap: wrap;
  gap: 6px; margin-top: 4px;
}
.role-chip {
  font-size: 10px; padding: 2px 8px; border-radius: 6px;
  background: var(--f7-surface-container, #f5f5f5); color: var(--f7-on-surface-variant, #8e8e93);
}
.role-chip.admin, .role-chip.owner {
  background: color-mix(in srgb, var(--f7-theme-color) 12%, transparent);
  color: var(--f7-theme-color); font-weight: 500;
}
.role-chip.guest { opacity: .7; }
.tier-label { font-size: 10px; font-weight: 500; }
.growth-chip {
  display: inline-flex; align-items: center; gap: 3px;
  font-size: 10px; font-weight: 600; padding: 2px 7px; border-radius: 8px;
}
.growth-chip--achievement {
  background: var(--f7-tertiary-container, #f5f0ff);
  color: var(--f7-on-tertiary-container, #6b46c1);
}
.nudge-btn { flex-shrink: 0; }
.menu-btn {
  flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  width: 32px; height: 32px; padding: 0; margin: 0;
  border: 0; border-radius: 50%;
  background: transparent;
  color: var(--f7-on-surface-variant, #8e8e93);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.menu-btn:active { background: var(--f7-list-item-pressed-bg-color, rgba(0, 0, 0, .08)); }
.menu-btn:focus-visible {
  outline: 2px solid var(--f7-theme-color);
  outline-offset: 1px;
}
</style>
